// CreateAmendmentModal — generic modal for any entity's amendment.
//
// Configurable via the `config.amendableFields` array. Each field is
// either text / number / textarea. A `validate(values, original)` hook
// can return a string error to short-circuit submission (used by the
// Journal version of this modal to enforce debit == credit before any
// network round-trip).
import React, { useState } from 'react';
import { X } from 'lucide-react';
import { toast } from 'sonner';
import { axiosInstance } from '../../App';

export default function CreateAmendmentModal({
  entity,
  config,
  onClose,
  onCreated,
}) {
  const [values, setValues] = useState(() => {
    const initial = {};
    for (const f of config.amendableFields) {
      initial[f.key] = entity[f.key] ?? '';
    }
    return initial;
  });
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);

  const handleChange = (key, val) => setValues((v) => ({ ...v, [key]: val }));

  const buildPatch = () => {
    const patch = {};
    for (const f of config.amendableFields) {
      const current = entity[f.key];
      const next = values[f.key];
      if (f.type === 'number') {
        const n = next === '' || next === null ? null : Number(next);
        if (n !== current) patch[f.key] = n;
      } else if (String(next ?? '') !== String(current ?? '')) {
        patch[f.key] = next === '' ? null : next;
      }
    }
    return patch;
  };

  const handleSubmit = async () => {
    const patch = buildPatch();
    if (Object.keys(patch).length === 0) {
      toast.error('لا يوجد تغييرات للحفظ');
      return;
    }
    // Optional client-side guardrail (e.g., journal balance).
    if (typeof config.validate === 'function') {
      const err = config.validate({ ...entity, ...patch }, entity, patch);
      if (err) {
        toast.error(err);
        return;
      }
    }
    setSaving(true);
    try {
      const res = await axiosInstance.post(
        `${config.endpointBase}/${entity[config.idField]}/amend`,
        { patch, reason: reason || null },
      );
      toast.success(`تم إنشاء الإصدار v${res.data.version.version_number}`);
      onCreated && onCreated(res.data.version);
      onClose();
    } catch (err) {
      const detail = err.response?.data?.detail;
      const msg =
        typeof detail === 'string' ? detail :
        detail?.reason || 'تعذّر الحفظ';
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  const numberLabel = entity[config.numberKey] || entity[config.idField];
  const accent = config.accentClass || 'bg-violet-600 hover:bg-violet-700';

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4"
      onClick={onClose}
      dir="rtl"
      data-testid={`${config.testIdPrefix}-amendment-modal`}
    >
      <div
        className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between p-5 border-b border-slate-200">
          <div>
            <div className="text-xs uppercase tracking-wide text-slate-400 mb-1">إنشاء تعديل · {config.entityLabel}</div>
            <div className="text-lg font-bold text-slate-900">{numberLabel}</div>
            <div className="text-xs text-slate-500 mt-1">
              التعديل لا يحلّ محل الإصدار الحالي حتى يتم ترحيله بشكل صريح.
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-500"
            data-testid={`${config.testIdPrefix}-amendment-close`}
          >
            <X size={18} />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-5 space-y-3">
          {config.amendableFields.map((f) => (
            <div key={f.key} className="grid grid-cols-[1fr_2fr] gap-3 items-start">
              <label className="text-sm text-slate-700 pt-2">{f.label}</label>
              {f.type === 'textarea' ? (
                <textarea
                  rows={3}
                  value={values[f.key] ?? ''}
                  onChange={(e) => handleChange(f.key, e.target.value)}
                  className="border border-slate-200 rounded-lg p-2 text-sm focus:ring-2 focus:ring-violet-500 outline-none"
                  data-testid={`${config.testIdPrefix}-amend-${f.key}`}
                />
              ) : (
                <input
                  type={f.type}
                  value={values[f.key] ?? ''}
                  onChange={(e) => handleChange(f.key, e.target.value)}
                  className="border border-slate-200 rounded-lg p-2 text-sm focus:ring-2 focus:ring-violet-500 outline-none"
                  data-testid={`${config.testIdPrefix}-amend-${f.key}`}
                />
              )}
            </div>
          ))}
          <div className="grid grid-cols-[1fr_2fr] gap-3 items-start pt-2 border-t border-slate-100">
            <label className="text-sm text-slate-700 pt-2">سبب التعديل</label>
            <input
              type="text"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="اختياري — يظهر في سجل التدقيق"
              className="border border-slate-200 rounded-lg p-2 text-sm focus:ring-2 focus:ring-violet-500 outline-none"
              data-testid={`${config.testIdPrefix}-amend-reason`}
            />
          </div>
        </div>
        <div className="p-4 border-t border-slate-200 flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 rounded-lg border border-slate-200 text-sm text-slate-700 hover:bg-slate-50"
          >إلغاء</button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={saving}
            data-testid={`${config.testIdPrefix}-amend-submit`}
            className={`px-4 py-2 rounded-lg text-white text-sm font-medium disabled:bg-slate-300 ${accent}`}
          >
            {saving ? 'جارٍ الحفظ…' : 'إنشاء التعديل'}
          </button>
        </div>
      </div>
    </div>
  );
}
